import React, { useMemo } from 'react'
import { ReactComponent as FolderIcon } from 'assets/icons/folder.svg'
import { ReactComponent as ClockIcon } from 'assets/icons/clock.svg'
import { ReactComponent as ArrowIcon } from 'assets/icons/arrow_short_right.svg'
import { ReactComponent as TrashIcon } from 'assets/icons/trash-2.svg'
import { getFinderToolIcon, getSortedFinderSteps } from '../../../../constants/researchFixtures'
import { CountryFlagIcon } from '../../../../constants/countryFlagIcons'
import {
	formatJourneyUpdated,
	getJourneyCareers,
	getJourneyCountries,
	getJourneyCourses,
} from '../../savedJourneyUtils'
import s from './ConsultantJourneyCard.module.scss'

const GROUP_META = {
	career: { label: 'Careers' },
	country: { label: 'Countries' },
	course: { label: 'Courses' },
}

function Group({ finderId, label, children }) {
	if (!children) return null
	const icon = getFinderToolIcon(finderId)
	return (
		<div className={s.card__group}>
			<div className={s.card__groupHead}>
				<span className={s.card__groupIcon}>{icon}</span>
				<span className={s.card__groupLabel}>{label}</span>
			</div>
			<div className={s.card__tags}>{children}</div>
		</div>
	)
}

export default function ConsultantJourneyCard({
	journey,
	isActive = false,
	onContinue,
	onDelete,
}) {
	const careers = getJourneyCareers(journey)
	const countries = getJourneyCountries(journey)
	const courses = getJourneyCourses(journey)
	const updated = formatJourneyUpdated(journey)
	const typeFlow = journey?.typeFlowJourney || 'career'

	const groups = useMemo(() => {
		const itemsByKey = {
			career: careers,
			country: countries,
			course: courses,
		}
		return getSortedFinderSteps(typeFlow)
			.filter(key => ['career', 'country', 'course'].includes(key))
			.map(key => ({
				key,
				label: `${GROUP_META[key].label} (${itemsByKey[key].length})`,
				items: itemsByKey[key],
			}))
			.filter(group => group.items.length > 0)
	}, [typeFlow, careers, countries, courses])

	return (
		<article className={`${s.card} ${isActive ? s.card_active : ''}`}>
			<header className={s.card__header}>
				<span className={s.card__folder}>
					<FolderIcon className={s.card__folderIcon} />
				</span>
				<div className={s.card__head}>
					<h3 className={s.card__title}>AI Explorer</h3>
					{updated ? (
						<p className={s.card__updated}>
							<ClockIcon className={s.card__clock} aria-hidden />
							<span>Updated {updated}</span>
						</p>
					) : null}
				</div>
			</header>

			<div className={s.card__body}>
				{groups.map(group => (
					<Group key={group.key} finderId={group.key} label={group.label}>
						{group.key === 'country'
							? group.items.map(item => (
									<span key={item} className={s.card__tag}>
										<CountryFlagIcon country={item} className={s.card__flag} />
										{item}
									</span>
								))
							: group.items.map(item => (
									<span key={item} className={s.card__tag}>
										{item}
									</span>
								))}
					</Group>
				))}
			</div>

			<footer className={s.card__footer}>
				<button
					type="button"
					className={s.card__delete}
					onClick={() => onDelete?.(journey.id)}
					aria-label="Delete saved journey"
				>
					<span className={s.card__deleteIcon}>
						<TrashIcon />
					</span>
					<span className={s.card__deleteText}>Delete</span>
				</button>
				<button
					type="button"
					className={s.card__resume}
					onClick={() => onContinue?.(journey)}
				>
					Resume back
					<ArrowIcon className={s.card__resumeIcon} aria-hidden />
				</button>
			</footer>
		</article>
	)
}
